/**
 * QuoteCustomerOfferCard — summary hero matching Quote Ops mock.
 */

import React, { useMemo } from "react";
import { Box, Card, CardContent, Chip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import QuoteOperationalStatusBadge from "./QuoteOperationalStatusBadge.jsx";
import {
  formatQuoteMoney,
  getQuoteDeposit,
  getQuoteDistanceLabel,
  getQuoteMoveTypeLabel,
  getQuoteTotal,
} from "../services/quoteOperationalStatus.js";
import { formatQuoteMoveDate } from "../utils/moveDateUtils.js";

const HeroCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  background:
    "linear-gradient(135deg, rgba(46,125,50,0.04) 0%, rgba(255,255,255,1) 45%)",
}));

const MoneyBlock = styled(Box)(({ theme }) => ({
  minWidth: 120,
  padding: theme.spacing(1.5, 2),
  borderRadius: 10,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
}));

const QuoteCustomerOfferCard = ({ quote }) => {
  const total = useMemo(() => getQuoteTotal(quote), [quote]);
  const deposit = useMemo(() => getQuoteDeposit(quote), [quote]);
  const depositPct =
    total > 0 && deposit > 0 ? Math.round((deposit / total) * 100) : null;
  const moveType = useMemo(() => getQuoteMoveTypeLabel(quote), [quote]);
  const distance = useMemo(() => getQuoteDistanceLabel(quote), [quote]);

  if (!quote) return null;

  return (
    <HeroCard elevation={0} data-testid="quote-customer-offer-card">
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <Box flex={1} minWidth={220}>
            <Chip
              label={moveType}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ mb: 1.5, fontWeight: 600 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {quote.from || "Origin"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ my: 0.25, fontWeight: 600 }}
            >
              ↓
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {quote.to || "Destination"}
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Move date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatQuoteMoveDate(quote)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Distance
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {distance}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {quote.customerName || "Unknown"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Quote
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontFamily: "monospace" }}
                >
                  {quote.id}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            alignItems={{ xs: "stretch", sm: "flex-end" }}
            gap={1.25}
          >
            <QuoteOperationalStatusBadge quote={quote} />
            <Box display="flex" flexWrap="wrap" gap={1.25}>
              <MoneyBlock>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: "success.dark" }}
                >
                  {formatQuoteMoney(total)}
                </Typography>
              </MoneyBlock>
              <MoneyBlock>
                <Typography variant="caption" color="text.secondary">
                  Deposit{depositPct != null ? ` (${depositPct}%)` : ""}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: "warning.dark" }}
                >
                  {deposit > 0 ? formatQuoteMoney(deposit) : "—"}
                </Typography>
              </MoneyBlock>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </HeroCard>
  );
};

export default QuoteCustomerOfferCard;
